import { yupResolver } from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';

import {
  MultipleSelect,
  NumberInput,
  Select,
  TextInput,
  TextareaInput,
} from 'components/FormControl';
import Loading from 'components/Loading';

import { create, getAllRoles, getById, update } from 'apis/employees.api';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import { getAll as getAllResearchFields } from 'apis/researchFields.api';
import { ROLES, TOAST } from 'constant';
import {
  addEmployeeValidationSchema,
  updateEmployeeValidationSchema,
} from 'features/Employee/validation';
import { toast } from 'layout';
import { getAcademicDegrees, getAcademicRanks, getRoles, mapError } from 'utils/func';

export default function AddForm({ selectedEmployee, visible, setVisible, isCreateMode, refetch }) {
  // #region Data
  const { t } = useTranslation();

  const userRoles = getRoles();
  const isChangeRoles = userRoles.some((role) => role === ROLES.ADMIN);

  const formOptions = {
    resolver: yupResolver(
      isCreateMode ? addEmployeeValidationSchema : updateEmployeeValidationSchema
    ),
  };

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm(formOptions);

  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(create);
  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(update);

  const { data: researchFieldsData } = useQuery(['researchFields'], getAllResearchFields);
  const researchFields = useMemo(
    () =>
      researchFieldsData?.data.map((e) => ({
        ...e,
        name: e.code ? t(`researchFields.${e.code}`) : e.name,
      })) ?? [],
    [researchFieldsData, t]
  );

  const { data: rolesData } = useQuery(['roles'], getAllRoles);
  const roles = useMemo(
    () =>
      rolesData?.data.map((e) => ({
        ...e,
        name: e.code ? t(`userRole.${e.code}`) : e.name,
      })) ?? [],
    [rolesData, t]
  );

  const { data: organizationsData } = useQuery(['organizations'], getAllOrganizations);
  const organizations = useMemo(
    () =>
      organizationsData?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );

  const statuses = Object.entries(t('employee.viewList.statuses', { returnObjects: true })).map(
    (item) => ({
      value: item[0],
      name: item[1],
    })
  );

  const genders = Object.entries(t('userProfile.gender', { returnObjects: true })).map((item) => ({
    value: item[0],
    name: item[1],
  }));

  const { data: employeeData } = useQuery(
    `employee${selectedEmployee?.id}`,
    () => getById(selectedEmployee?.id),
    {
      enabled: isCreateMode === false,
    }
  );
  const employee = useMemo(() => employeeData?.data ?? {}, [employeeData]);

  const organizationId = watch('organizationId');
  const currentOrganization = useMemo(() => {
    if (organizations?.length) {
      const result = organizations?.find((item) => item?.id === organizationId) ?? {};
      setValue('organizationEnglishName', result?.englishName ?? '');
      if (result?.englishName !== 'Other') {
        setValue('specificOrganizationName', null);
      }
      return result;
    }
    return null;
  }, [organizations, organizationId]);

  useEffect(() => {
    reset({
      fullname: employee?.fullname ?? '',
      email: employee?.email ?? '',
      researchFieldIds:
        employee?.user?.userResearchFields?.map((item) => item.researchFieldId) ?? [],
      roleIds: employee?.user?.userRoles?.map((item) => item.role.id) ?? [],
      organizationId: employee?.organizationId ?? '',
      specificOrganizationName: employee?.specificOrganizationName ?? '',
      gender: employee?.scientificProfile?.gender ?? '',
      phone: employee?.scientificProfile?.personalPhone ?? '',
      academicRank: employee?.scientificProfile?.academicRank ?? '',
      academicDegree: employee?.scientificProfile?.academicDegree ?? '',
      academicRankYear: employee?.scientificProfile?.academicRankYear ?? 0,
      academicDegreeYear: employee?.scientificProfile?.academicDegreeYear ?? 0,
      status: employee?.user?.status ?? '',
    });
  }, [employee]);

  useEffect(() => {
    if (isCreateMode && !isChangeRoles) {
      reset({
        roleIds: isCreateMode && !isChangeRoles ? ['54148d0b-87c7-4fec-b529-3c20dead1208'] : [],
      });
    }
  }, []);
  // #endregion

  // #region Event
  const checkKeyDown = (e) => {
    if (e.code === 'Enter') e.preventDefault();
  };

  const onSubmit = async (data) => {
    if (isCreateMode) {
      const newData = Object.fromEntries(
        Object.entries(data).filter(
          ([, value]) => value !== null && value !== undefined && value !== ''
        )
      );

      mutateCreate(newData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('employee.create.createSuccess'));
          reset();
          refetch();
          setVisible(false);
        },
        onError: async (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    } else {
      const { email, organizationEnglishName, ...rest } = data;
      const newData = {
        body: Object.fromEntries(
          Object.entries(rest).filter(
            ([, value]) => value !== null && value !== undefined && value !== ''
          )
        ),
        id: selectedEmployee?.id,
      };

      mutateUpdate(newData, {
        onSuccess: () => {
          toast(TOAST.SUCCESS, t('employee.create.updateSuccess'));
          refetch();
          setVisible(false);
        },
        onError: (error) => {
          toast(TOAST.ERROR, mapError(t, error));
        },
      });
    }
  };
  // #endregion

  return (
    <Dialog
      header={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <p className="text-blue-500">
          {isCreateMode ? t('employee.create.title') : t('employee.update.title')}
        </p>
      }
      visible={visible}
      draggable={false}
      style={{ width: '500px' }}
      onHide={() => setVisible(false)}
    >
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={checkKeyDown}
          className="p-fluid flex flex-column"
          autoComplete="off"
        >
          <div className="grid">
            <div className="col-6">
              <TextInput
                label={t('employee.fullname')}
                name="fullname"
                autoFocus
                control={control}
                errors={errors}
                isRequired
              />
            </div>
            <div className="col-6">
              <TextInput
                label={t('employee.email')}
                name="email"
                inputField="value"
                control={control}
                errors={errors}
                disabled={!isCreateMode}
                isRequired
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-6">
              {isCreateMode ? (
                <TextInput
                  label={t('employee.create.password')}
                  name="password"
                  inputField="value"
                  type="password"
                  control={control}
                  errors={errors}
                  isRequired
                />
              ) : (
                <Select
                  label={t('employee.update.status')}
                  name="status"
                  inputField="value"
                  renderField="name"
                  options={statuses}
                  control={control}
                  errors={errors}
                  isRequired
                />
              )}
            </div>
            <div className="col-6">
              <MultipleSelect
                label={t('employee.roles')}
                name="roleIds"
                options={roles}
                inputField="id"
                renderField="name"
                control={control}
                errors={errors}
                style={{ flex: '0 0 auto', width: '41.6667%' }}
                isRequired
                disabled={!isChangeRoles}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-6">
              <Select
                label={t('employee.update.chiefAcademicRank')}
                name="academicRank"
                inputField="value"
                renderField="name"
                options={getAcademicRanks(t)}
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-6">
              <NumberInput
                label={t('employee.update.chiefAcademicRankYear')}
                name="academicRankYear"
                min={0}
                max={new Date().getFullYear()}
                control={control}
                errors={errors}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-6">
              <Select
                label={t('employee.update.chiefAcademicDegree')}
                name="academicDegree"
                inputField="value"
                renderField="name"
                options={getAcademicDegrees(t)}
                control={control}
                errors={errors}
                s
                isRequired
              />
            </div>
            <div className="col-6">
              <NumberInput
                label={t('employee.update.chiefAcademicDegreeYear')}
                name="academicDegreeYear"
                min={0}
                max={new Date().getFullYear()}
                control={control}
                errors={errors}
              />
            </div>
          </div>
          <Select
            label={t('employee.organization')}
            name="organizationId"
            options={organizations}
            inputField="id"
            renderField="name"
            control={control}
            errors={errors}
            isRequired
          />

          {currentOrganization?.englishName === 'Other' && (
            <div className="mb-2">
              <TextareaInput
                label={t('employee.specificOrganizationName')}
                name="specificOrganizationName"
                type="text"
                control={control}
                errors={errors}
                isRequired
              />
            </div>
          )}

          <MultipleSelect
            label={t('employee.researchFields')}
            name="researchFieldIds"
            options={researchFields}
            inputField="id"
            renderField="name"
            control={control}
            errors={errors}
          />

          <div className="grid">
            <div className="col-6">
              <Select
                label={t('employee.gender')}
                name="gender"
                options={genders}
                inputField="value"
                control={control}
                errors={errors}
              />
            </div>
            <div className="col-6">
              <TextInput
                label={t('employee.phone')}
                name="phone"
                control={control}
                errors={errors}
                mask={''.padStart(10, '9')}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-6">
              <Button
                label={t('employee.cancel')}
                type="button"
                severity="danger"
                onClick={() => {
                  reset();
                  setVisible(false);
                }}
              />
            </div>
            <div className="col-6">
              <Button
                label={t('employee.save')}
                icon="pi pi-pencil"
                className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                type="submit"
              />
            </div>
          </div>
        </form>
        {(isLoadingCreate || isLoadingUpdate) && <Loading />}
      </div>
    </Dialog>
  );
}

AddForm.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types, react/require-default-props
  selectedEmployee: PropTypes.object,
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  isCreateMode: PropTypes.bool.isRequired,
  refetch: PropTypes.func.isRequired,
};
